/*
 * NativeDSD audio
 *
 * One shared audio engine for the whole site (window.NativeDSDAudio): a single <audio>, a queue,
 * next/prev, auto-advance, and state kept in sessionStorage so a track can be resumed on the next page.
 * Anything that wants to play audio (blog posts now; product tracklists or "random track" later)
 * calls the engine instead of creating its own Audio object.
 *
 * Track: { src, title, subtitle?, artwork?, url? }
 * Events on document: ndsd-audio:track | play | pause | time | ended | stop | error  (detail = state)
 *
 * Also included:
 * - blog players: every <audio> and every paragraph that is only a link to an audio file in Shopify Files inside
 *   .article__content becomes a styled player; a fully italic paragraph right after it is used as the title
 * - mini-player: a floating bar that shows while a track is loaded (and its own player is off screen),
 *   and after navigating to another page, paused at the same spot
 */
(() => {
  if (window.NativeDSDAudio) return;

  const KEY = 'ndsd-audio:v1';
  const AUDIO_EXT = /\.(mp3|m4a|aac|ogg|oga|opus|wav|flac)(\?|#|$)/i;

  const store = {
    get() {
      try { return JSON.parse(sessionStorage.getItem(KEY)); } catch (e) { return null; }
    },
    set(value) {
      try {
        value ? sessionStorage.setItem(KEY, JSON.stringify(value)) : sessionStorage.removeItem(KEY);
      } catch (e) {}
    },
  };

  const fmt = (s) => {
    if (!isFinite(s) || s < 0) return '–:––';
    s = Math.floor(s);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = String(s % 60).padStart(2, '0');
    return h ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
  };

  // "wp271521-02_The-Heart-Speaks_320kbps-2ch-1.mp3" -> "The Heart Speaks"
  const prettyName = (src) =>
    decodeURIComponent(src.split(/[?#]/)[0].split('/').pop() || '')
      .replace(/\.[a-z0-9]+$/i, '')
      .replace(/^wp\d+-/, '')
      .replace(/[_-]?\d+\s?kbps.*$/i, '')
      .replace(/^\d+[_\s.-]+/, '')
      .replace(/[-_]+/g, ' ')
      .trim() || 'Audio';

  const meta = (prop) => document.querySelector(`meta[property="${prop}"]`)?.content || '';

  /* ---------------- engine ---------------- */

  const audio = new Audio();
  audio.preload = 'metadata';
  let queue = [];
  let queueOptions = {};   // per queue: { autoAdvance: true | false | () => boolean } (default: carry on to the next track)
  let index = -1;
  let lastSave = 0;
  // true while the visitor wants music: set on play, cleared only by their own pause/stop (or the end of the queue),
  // so a page change (which stops the sound) doesn't count as a pause and the next page can carry on
  let wantPlaying = false;

  const current = () => queue[index] || null;
  const duration = () => (isFinite(audio.duration) ? audio.duration : 0);
  const state = () => ({
    track: current(),
    index,
    queue,
    time: audio.currentTime || 0,
    duration: duration(),
    paused: audio.paused,
  });
  const emit = (name) => document.dispatchEvent(new CustomEvent(`ndsd-audio:${name}`, { detail: state() }));

  const save = (force) => {
    const now = Date.now();
    if (!force && now - lastSave < 1000) return;
    lastSave = now;
    store.set(current() ? { queue, index, time: audio.currentTime || 0, playing: wantPlaying } : null);
  };

  const setMediaSession = (track) => {
    if (!('mediaSession' in navigator) || !track) return;
    try {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: track.title || '',
        artist: track.subtitle || 'NativeDSD',
        artwork: track.artwork ? [{ src: track.artwork }] : [],
      });
    } catch (e) {}
  };

  const setTrack = (i, { autoplay = true, time = 0 } = {}) => {
    if (!queue[i]) return;
    index = i;
    audio.src = queue[i].src;
    if (time) {
      audio.addEventListener('loadedmetadata', () => { audio.currentTime = Math.min(time, duration() || time); emit('time'); }, { once: true });
    }
    if (autoplay) audio.play().catch(() => emit('pause'));
    else audio.load();
    setMediaSession(queue[i]);
    save(true);
    emit('track');
  };

  // Previews are for logged in customers (window.ndsdAudioAccess from layout/theme.liquid): logged out, any attempt to
  // play asks the visitor to log in (the dialog from assets/theme.js) and nothing starts.
  const needsLogin = () => !!(window.ndsdAudioAccess && window.ndsdAudioAccess.loginRequired);
  const askLogin = () => {
    const url = new URL((window.ndsdAudioAccess && window.ndsdAudioAccess.loginUrl) || '/account/login', location.origin);
    if (!url.searchParams.has('return_to')) url.searchParams.set('return_to', location.pathname + location.search);
    if (window.ndsdLoginDialog) {
      window.ndsdLoginDialog.open({
        title: 'Log in to listen',
        text: 'Track previews are for customers with an account. Log in or create one, and you come straight back to this page.',
        url: url.toString(),
      });
    } else {
      location.href = url.toString();
    }
  };

  const api = {
    audio,
    state,
    current,
    format: fmt,
    prettyName,
    playQueue(tracks, start = 0, options = {}) {
      if (needsLogin()) return askLogin();
      queue = tracks.map((t) => ({ ...t }));
      queueOptions = options || {};
      setTrack(start);
    },
    play(track) {
      api.playQueue([track], 0);
    },
    isCurrent(src) {
      return !!src && current()?.src === src;
    },
    toggle() {
      if (!current()) return;
      if (audio.paused) {
        audio.play().catch(() => emit('pause'));
      } else {
        wantPlaying = false;
        audio.pause();
      }
    },
    pause() {
      wantPlaying = false;
      audio.pause();
    },
    hasNext() {
      return index < queue.length - 1;
    },
    hasPrev() {
      return index > 0;
    },
    next() {
      if (api.hasNext()) setTrack(index + 1);
    },
    prev() {
      if (audio.currentTime > 3 || !api.hasPrev()) {
        audio.currentTime = 0;
        emit('time');
      } else {
        setTrack(index - 1);
      }
    },
    seek(seconds) {
      if (duration()) audio.currentTime = Math.max(0, Math.min(seconds, duration()));
    },
    stop() {
      wantPlaying = false;
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
      queue = [];
      queueOptions = {};
      index = -1;
      store.set(null);
      emit('stop');
    },
  };

  audio.addEventListener('play', () => { wantPlaying = true; emit('play'); });
  audio.addEventListener('pause', () => { save(true); emit('pause'); });
  audio.addEventListener('timeupdate', () => { save(); emit('time'); });
  audio.addEventListener('loadedmetadata', () => emit('time'));
  audio.addEventListener('ended', () => {
    const auto = typeof queueOptions.autoAdvance === 'function' ? queueOptions.autoAdvance() : queueOptions.autoAdvance !== false;
    if (auto && api.hasNext()) return api.next();
    wantPlaying = false;
    audio.currentTime = 0;
    save(true);
    emit('ended');
  });
  audio.addEventListener('error', () => { if (current()) emit('error'); });
  window.addEventListener('pagehide', () => save(true));

  if ('mediaSession' in navigator) {
    const handlers = {
      play: () => api.toggle(),
      pause: () => api.pause(),
      nexttrack: () => api.next(),
      previoustrack: () => api.prev(),
      seekto: (d) => api.seek(d.seekTime),
    };
    Object.entries(handlers).forEach(([action, fn]) => {
      try { navigator.mediaSession.setActionHandler(action, fn); } catch (e) {}
    });
  }

  window.NativeDSDAudio = api;

  /* ---------------- shared UI bits ---------------- */

  const ICONS = {
    play: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M8 5.5v13l11-6.5z" fill="currentColor"/></svg>',
    pause: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M7 5h3.5v14H7zM13.5 5H17v14h-3.5z" fill="currentColor"/></svg>',
    prev: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 5h2v14H6zM19 5.5v13L9.5 12z" fill="currentColor"/></svg>',
    next: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M16 5h2v14h-2zM5 5.5v13l9.5-6.5z" fill="currentColor"/></svg>',
    close: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6.4 5 5 6.4 10.6 12 5 17.6 6.4 19 12 13.4l5.6 5.6 1.4-1.4-5.6-5.6L19 6.4 17.6 5 12 10.6z" fill="currentColor"/></svg>',
  };

  const setProgress = (range, time, dur) => {
    range.max = dur || 0;
    range.value = dur ? time : 0;
    range.disabled = !dur;
    range.style.setProperty('--p', dur ? `${(time / dur) * 100}%` : '0%');
  };

  const bindSeek = (range, isMine) => {
    // pause progress updates while dragging so the thumb doesn't jump back
    range.addEventListener('input', () => {
      range.dataset.dragging = '1';
      range.style.setProperty('--p', `${(range.value / (range.max || 1)) * 100}%`);
    });
    range.addEventListener('change', () => {
      delete range.dataset.dragging;
      if (isMine()) api.seek(Number(range.value));
    });
  };

  /* ---------------- blog players ---------------- */

  const players = [];

  const captionAfter = (block) => {
    const next = block.nextElementSibling;
    if (!next || next.tagName !== 'P' || next.children.length !== 1) return null;
    const only = next.children[0];
    if (!/^(EM|I)$/.test(only.tagName)) return null;
    const text = next.textContent.trim();
    return text && text === only.textContent.trim() && text.length <= 200 ? next : null;
  };

  const buildPlayer = (src, title) => {
    const el = document.createElement('div');
    el.className = 'ndsd-player';
    el.innerHTML = `
      <button type="button" class="ndsd-player__toggle" aria-label="Play">${ICONS.play}${ICONS.pause}</button>
      <div class="ndsd-player__body">
        <p class="ndsd-player__title"></p>
        <input type="range" class="ndsd-range ndsd-player__seek" min="0" max="0" step="0.1" value="0" aria-label="Seek" disabled>
        <p class="ndsd-player__time"><span class="ndsd-player__current">0:00</span><span class="ndsd-player__duration">–:––</span></p>
      </div>`;
    el.querySelector('.ndsd-player__title').textContent = title;
    return el;
  };

  /* album of a blog track: a product link in the italic caption, else the post's only album card */

  const productHandle = (href) => {
    try {
      const m = new URL(href, location.href).pathname.match(/\/products\/([^/?#]+)/);
      return m ? m[1] : null;
    } catch (e) {
      return null;
    }
  };

  // the post's album cards (snippets/album-shortcode-card.liquid): handle -> { title, image, url }
  const cardAlbums = (content) => {
    const albums = new Map();
    content.querySelectorAll('.blog-album-card').forEach((card) => {
      const link = card.querySelector('.blog-album-card__media');
      const handle = link && productHandle(link.getAttribute('href'));
      if (!handle || albums.has(handle)) return;
      const img = card.querySelector('img.blog-album-card__cover, img');
      albums.set(handle, {
        title: card.querySelector('.blog-album-card__title')?.textContent.trim() || '',
        image: img ? img.currentSrc || img.src : '',
        url: link.getAttribute('href'),
      });
    });
    return albums;
  };

  const albumRequests = {};
  const fetchAlbum = (handle) =>
    (albumRequests[handle] ||= fetch(`/products/${handle}.js`)
      .then((r) => (r.ok ? r.json() : null))
      .then((p) => {
        if (!p) return null;
        let image = p.featured_image || '';
        if (image.startsWith('//')) image = `https:${image}`;
        if (image) image += `${image.includes('?') ? '&' : '?'}width=240`;
        return { title: p.title, image, url: `/products/${handle}` };
      })
      .catch(() => null));

  // "Three Pictures: I Rue Lafayette from the album ‘A Desperate Light’" -> "Three Pictures: I Rue Lafayette"
  const withoutFromAlbum = (title) =>
    title.replace(/\s*[(,–—-]?\s*from\s+(the\s+)?(album\s+)?[‘'"“][^’'"”]+[’'"”]\)?\s*$/i, '').trim() || title;

  const setAlbum = (player, album) => {
    if (!album || player.album) return;
    player.album = album;
    const t = player.track;
    t.title = withoutFromAlbum(t.title);
    t.subtitle = album.title;
    t.artwork = album.image || t.artwork;
    t.url = album.url;
    const titleEl = player.el.querySelector('.ndsd-player__title');
    titleEl.textContent = t.title;
    const from = document.createElement('a');
    from.className = 'ndsd-player__from';
    from.href = album.url;
    from.innerHTML = 'From the album <span></span>';
    from.querySelector('span').textContent = album.title;
    titleEl.after(from);
    if (album.image) {
      const cover = document.createElement('a');
      cover.className = 'ndsd-player__cover';
      cover.href = album.url;
      cover.setAttribute('aria-label', `Album: ${album.title}`);
      const img = new Image();
      img.src = album.image;
      img.alt = '';
      img.loading = 'lazy';
      cover.append(img);
      player.el.append(cover);
    }
    // a queue that is already playing (or restored) keeps its own copies of the tracks
    queue.forEach((q) => {
      if (q.src === t.src) Object.assign(q, { title: t.title, subtitle: t.subtitle, artwork: t.artwork, url: t.url });
    });
    if (current()?.src === t.src) setMediaSession(current());
    renderMini();
  };

  const initBlogPlayers = () => {
    const content = document.querySelector('.article__content');
    if (!content) return;

    const found = [];
    content.querySelectorAll('audio').forEach((a) => {
      const src = a.currentSrc || a.getAttribute('src') || a.querySelector('source')?.getAttribute('src');
      if (!src) return;
      const block = a.parentElement.tagName === 'P' && a.parentElement.textContent.trim() === '' ? a.parentElement : a;
      found.push({ block, src, title: a.dataset.title || a.title || '' });
    });
    // links only for files uploaded to Shopify Files: links elsewhere are often big downloads (test files, hi-res WAVs)
    const isShopifyFile = (url) => url.hostname === 'cdn.shopify.com' || (url.origin === location.origin && url.pathname.startsWith('/cdn/'));
    content.querySelectorAll('p > a[href]').forEach((link) => {
      const p = link.parentElement;
      if (!AUDIO_EXT.test(link.href) || !isShopifyFile(new URL(link.href, location.href))) return;
      if (p.children.length !== 1 || p.textContent.trim() !== link.textContent.trim()) return;
      const text = link.textContent.trim();
      found.push({ block: p, src: link.href, title: /^https?:|\.\w{3,4}$/.test(text) ? '' : text });
    });

    found
      .filter(({ block }) => !block.closest('.blog-album-card')) // never build a player inside an album card
      .sort((a, b) => (a.block.compareDocumentPosition(b.block) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1))
      .forEach(({ block, src, title }) => {
        const caption = captionAfter(block);
        const albumLink = caption?.querySelector('a[href*="/products/"]');
        let name = title || caption?.textContent.trim() || prettyName(src);
        if (!title && albumLink) {
          // "Captain Campbell’s March, from the album <a>Haydn Op. 77</a>" -> "Captain Campbell’s March"
          const text = caption.textContent;
          const at = text.lastIndexOf(albumLink.textContent);
          const rest = at < 0 ? text : text.slice(0, at) + text.slice(at + albumLink.textContent.length);
          name = rest.replace(/[\s,–—(:-]*(from\s+(the\s+)?(album\s+)?)?[‘’'"“”)\s]*$/i, '').trim() || name;
        }
        const el = buildPlayer(src, name);
        block.replaceWith(el);
        caption?.remove();
        players.push({
          el,
          albumHandle: albumLink ? productHandle(albumLink.getAttribute('href')) : null,
          track: {
            src,
            title: name,
            subtitle: meta('og:title').replace(/\s*[–|-]\s*NativeDSD.*$/i, ''),
            artwork: meta('og:image:secure_url') || meta('og:image'),
            url: location.pathname,
          },
        });
      });

    players.forEach((p, i) => {
      const toggle = p.el.querySelector('.ndsd-player__toggle');
      const seek = p.el.querySelector('.ndsd-player__seek');
      toggle.addEventListener('click', () => {
        if (api.isCurrent(p.track.src)) api.toggle();
        else api.playQueue(players.map((x) => x.track), i);
      });
      bindSeek(seek, () => api.isCurrent(p.track.src));
    });

    const albums = cardAlbums(content);
    const onlyAlbum = albums.size === 1 ? [...albums.values()][0] : null;
    players.forEach((p) => {
      if (p.albumHandle) {
        const fromCard = albums.get(p.albumHandle);
        if (fromCard) setAlbum(p, fromCard);
        else fetchAlbum(p.albumHandle).then((album) => setAlbum(p, album));
      } else if (onlyAlbum) {
        setAlbum(p, onlyAlbum);
      }
    });
  };

  const renderPlayers = () => {
    const s = state();
    players.forEach((p) => {
      const mine = api.isCurrent(p.track.src);
      const playing = mine && !s.paused;
      p.el.classList.toggle('is-current', mine);
      p.el.classList.toggle('is-playing', playing);
      p.el.querySelector('.ndsd-player__toggle').setAttribute('aria-label', playing ? 'Pause' : 'Play');
      const seek = p.el.querySelector('.ndsd-player__seek');
      if (!seek.dataset.dragging) setProgress(seek, mine ? s.time : 0, mine ? s.duration : 0);
      p.el.querySelector('.ndsd-player__current').textContent = fmt(mine ? s.time : 0);
      if (mine && s.duration) p.el.querySelector('.ndsd-player__duration').textContent = fmt(s.duration);
    });
  };

  /* ---------------- mini-player ---------------- */

  let mini = null;
  let currentPlayerVisible = false;

  const visibility = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((e) => { e.target._ndsdVisible = e.isIntersecting; });
        renderMini();
      })
    : null;

  const buildMini = () => {
    mini = document.createElement('div');
    mini.className = 'ndsd-mini';
    mini.setAttribute('role', 'region');
    mini.setAttribute('aria-label', 'Audio player');
    mini.hidden = true;
    mini.innerHTML = `
      <img class="ndsd-mini__art" alt="" width="48" height="48" hidden>
      <div class="ndsd-mini__text">
        <a class="ndsd-mini__title"></a>
        <p class="ndsd-mini__subtitle"></p>
      </div>
      <div class="ndsd-mini__controls">
        <button type="button" class="ndsd-mini__btn ndsd-mini__prev" aria-label="Previous track">${ICONS.prev}</button>
        <button type="button" class="ndsd-mini__btn ndsd-mini__toggle" aria-label="Play">${ICONS.play}${ICONS.pause}</button>
        <button type="button" class="ndsd-mini__btn ndsd-mini__next" aria-label="Next track">${ICONS.next}</button>
      </div>
      <p class="ndsd-mini__time"></p>
      <button type="button" class="ndsd-mini__btn ndsd-mini__close" aria-label="Close player">${ICONS.close}</button>
      <input type="range" class="ndsd-range ndsd-mini__seek" min="0" max="0" step="0.1" value="0" aria-label="Seek" disabled>`;
    mini.querySelector('.ndsd-mini__toggle').addEventListener('click', () => api.toggle());
    mini.querySelector('.ndsd-mini__prev').addEventListener('click', () => goPrev());
    mini.querySelector('.ndsd-mini__next').addEventListener('click', () => goNext());
    mini.querySelector('.ndsd-mini__close').addEventListener('click', () => api.stop());
    bindSeek(mini.querySelector('.ndsd-mini__seek'), () => !!current());
    document.body.appendChild(mini);
  };

  function renderMini() {
    const s = state();
    const player = players.find((p) => api.isCurrent(p.track.src));
    currentPlayerVisible = !!player?.el._ndsdVisible;
    const show = !!s.track && !currentPlayerVisible;
    if (!mini) {
      if (!show) return;
      buildMini();
    }
    mini.hidden = !show;
    document.body.classList.toggle('ndsd-mini-open', show);
    if (!s.track) return;

    const t = s.track;
    const title = mini.querySelector('.ndsd-mini__title');
    if (title.textContent !== t.title) title.textContent = t.title;
    if (t.url && t.url !== location.pathname) title.href = t.url;
    else title.removeAttribute('href');
    mini.querySelector('.ndsd-mini__subtitle').textContent = t.subtitle || '';
    const art = mini.querySelector('.ndsd-mini__art');
    if (t.artwork) {
      if (art.getAttribute('src') !== t.artwork) art.src = t.artwork;
      art.hidden = false;
    } else {
      art.hidden = true;
    }
    mini.classList.toggle('is-playing', !s.paused);
    mini.querySelector('.ndsd-mini__toggle').setAttribute('aria-label', s.paused ? 'Play' : 'Pause');
    // a playlist (several tracks) steps through its queue; a single preview from a play button on the page
    // (homepage cards, journal album cards) steps to the previous/next play button on the page
    const list = s.queue.length < 2 ? pageButtons() : null;
    const pi = list ? pageIndex(list) : -1;
    const pageMode = !!list && pi >= 0 && list.length > 1;
    const stepping = s.queue.length >= 2 || pageMode;
    mini.querySelector('.ndsd-mini__prev').hidden = !stepping;
    mini.querySelector('.ndsd-mini__next').hidden = !stepping;
    mini.querySelector('.ndsd-mini__next').disabled = pageMode ? pi >= list.length - 1 : !api.hasNext();
    mini.querySelector('.ndsd-mini__time').textContent = `${fmt(s.time)} / ${fmt(s.duration)}`;
    const seek = mini.querySelector('.ndsd-mini__seek');
    if (!seek.dataset.dragging) setProgress(seek, s.time, s.duration);
  }

  /* ---------------- play buttons anywhere: [data-ndsd-play data-src data-title data-subtitle data-artwork data-url] ---------------- */

  const trackFrom = (btn) => ({
    src: btn.dataset.src,
    title: btn.dataset.title || prettyName(btn.dataset.src),
    subtitle: btn.dataset.subtitle || '',
    artwork: btn.dataset.artwork || '',
    url: btn.dataset.url || location.pathname,
  });

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-ndsd-play]');
    if (!btn || !btn.dataset.src) return;
    e.preventDefault();
    if (btn.ndsdQueue) {
      // album button (cover of a product card): any track of its album being current means pause / resume
      if (btn.ndsdQueue.some((t) => api.isCurrent(t.src))) api.toggle();
      else api.playQueue(btn.ndsdQueue, 0);
    } else if (api.isCurrent(btn.dataset.src)) api.toggle();
    else api.play(trackFrom(btn));
  });

  /* journal album cards: [data-ndsd-lazy-play data-album-url] has no track yet; the first click fetches the
     album's first preview (templates/product.ndsd-preview.liquid) and turns it into a normal [data-ndsd-play] button */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-ndsd-lazy-play]');
    if (!btn || btn.dataset.src || btn.dataset.loading) return;
    e.preventDefault();
    if (needsLogin()) return askLogin();
    btn.dataset.loading = '1';
    btn.classList.add('is-loading');
    try {
      const url = new URL(btn.dataset.albumUrl, location.origin);
      url.searchParams.set('view', 'ndsd-preview');
      const track = await fetch(url.pathname + url.search, { credentials: 'same-origin' }).then((r) => r.json());
      if (!track || !track.src) {
        btn.hidden = true;
        return;
      }
      btn.dataset.src = track.src;
      btn.dataset.title = track.title || '';
      btn.dataset.subtitle = track.subtitle || '';
      btn.dataset.artwork = track.artwork || '';
      btn.dataset.url = track.url || url.pathname;
      btn.setAttribute('data-ndsd-play', '');
      if (Array.isArray(track.tracks) && track.tracks.length > 1) {
        // the whole album: next / previous move through its previews
        btn.ndsdQueue = track.tracks.map((t) => ({
          src: t.src,
          title: t.title || prettyName(t.src),
          subtitle: btn.dataset.subtitle,
          artwork: btn.dataset.artwork,
          url: btn.dataset.url,
        }));
        api.playQueue(btn.ndsdQueue, 0);
      } else {
        api.play(trackFrom(btn));
      }
    } catch (err) {
      btn.hidden = true;
    } finally {
      delete btn.dataset.loading;
      btn.classList.remove('is-loading');
    }
  });

  // previous / next for single previews: the play buttons on this page, in page order
  const pageButtons = () =>
    Array.from(document.querySelectorAll('[data-ndsd-play], [data-ndsd-lazy-play]')).filter(
      (b) => !b.hidden && b.offsetParent !== null
    );
  const pageIndex = (list) => list.findIndex((b) => b.dataset.src && api.isCurrent(b.dataset.src));
  const pageStep = (direction) => {
    const list = pageButtons();
    const target = list[pageIndex(list) + direction];
    if (!target) return false;
    target.click();
    return true;
  };
  function goNext() {
    if (state().queue.length > 1) api.next();
    else pageStep(1);
  }
  function goPrev() {
    if (state().queue.length > 1) return api.prev();
    // like a music player: back to the start first, the previous album when already at the start
    if (state().time > 3 || !pageStep(-1)) api.seek(0);
  }

  const renderButtons = () => {
    const s = state();
    document.querySelectorAll('[data-ndsd-play]').forEach((btn) => {
      const srcs = btn.ndsdQueue ? btn.ndsdQueue.map((t) => t.src) : [btn.dataset.src];
      const playing = srcs.some((src) => api.isCurrent(src)) && !s.paused;
      btn.classList.toggle('is-playing', playing);
      btn.setAttribute('aria-pressed', playing ? 'true' : 'false');
    });
  };

  /* ---------------- wiring ---------------- */

  const render = () => {
    renderPlayers();
    renderButtons();
    renderMini();
  };
  ['track', 'play', 'pause', 'time', 'ended', 'stop', 'error'].forEach((name) =>
    document.addEventListener(`ndsd-audio:${name}`, render)
  );

  const start = () => {
    initBlogPlayers();
    players.forEach((p) => visibility?.observe(p.el));

    // resume where the visitor left off on the previous page. If it was playing, try to carry on: Chrome/Edge
    // usually allow that once the visitor has clicked on the site; Safari blocks it, then it waits paused.
    const saved = needsLogin() ? null : store.get();
    if (saved?.queue?.length && saved.queue[saved.index]) {
      queue = saved.queue;
      setTrack(saved.index, { autoplay: false, time: saved.time });
      if (saved.playing) {
        audio.addEventListener('loadedmetadata', () => {
          audio.play().catch(() => { wantPlaying = false; save(true); emit('pause'); });
        }, { once: true });
      }
    }
    render();
  };

  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', start) : start();
})();
